'use client'

import { createContext, useContext, useState, type ReactNode } from 'react'

import {
  type LangCode,
  type TranslationKeys,
  languages,
  translations,
} from './translations'

type LanguageContextType = {
  lang: LangCode
  setLang: (lang: LangCode) => void
  t: TranslationKeys
}

const validCodes = new Set(languages.map((l) => l.code))

function detectBrowserLanguage(): LangCode {
  const browserLangs = navigator.languages || [navigator.language]
  for (const bl of browserLangs) {
    const code = bl.split('-')[0].toLowerCase()
    if (validCodes.has(code as LangCode)) {
      return code as LangCode
    }
  }
  return 'en'
}

const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  setLang: () => {},
  t: translations.en,
})

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<LangCode>(() => detectBrowserLanguage())
  const t = translations[lang]

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  return useContext(LanguageContext)
}
